import { useEffect, useRef, useState, type RefObject } from 'react'

/**
 * useReducedMotion — a ÚNICA leitura de prefers-reduced-motion no app (ADR-004). Reduced-motion é opt-in:
 * quem anima pergunta aqui e, se true, renderiza o estado final estático.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const on = () => setReduced(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return reduced
}

/**
 * useInView — disparo one-shot por IntersectionObserver (threshold 0,15; rootMargin -15% embaixo; unobserve após disparar).
 * É o caminho TESTADO do movimento em todos os navegadores (Chromium e Firefox); animation-timeline: view() é só
 * progressivo para a régua e vive atrás de @supports (ADR-004).
 */
export function useInView<T extends Element>(options: { once?: boolean } = {}): [RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null)
  const [inView, setInView] = useState(false)
  const once = options.once ?? true
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { setInView(true); if (once) io.unobserve(e.target) }
          else if (!once) setInView(false)
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -15% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [once])
  return [ref, inView]
}
